/** Нормализация / сериализация фильтров списка машин (URL + Query) + table state. */

export const CARS_TABLE_STORAGE_KEY = 'carsTableState'
export const CARS_TABLE_STATE_VERSION = 1
export const CARS_FILTERS_STORAGE_KEY = 'carsFilters'
export const CARS_FILTERS_VERSION = 1
export const CARS_PAGE_SIZE = 50

export const CARS_COLUMN_IDS = [
  'number',
  'avtocod_status',
  'black_list',
  'black_list_client',
  'ownerPhone',
  'vin',
]

export const CARS_COLUMN_LABELS = {
  number: 'Гос Номер',
  avtocod_status: 'Статус проверки Автокод',
  black_list: 'Черный список',
  black_list_client: 'Клиентский ЧС',
  ownerPhone: 'Клиентский номер телефона',
  vin: 'VIN-номер',
}

export function defaultCarsFilters() {
  return {
    query: '',
    queryCarNumber: '',
    queryVIN: '',
    queryPhoneNumber: '',
    autocodeStatus: 1,
    page: 1,
  }
}

function normalizeAutocodeStatus(value, fallback = 1) {
  if (value === '' || value == null) return fallback
  const num = Number(value)
  if (num === 0 || num === -1 || num === 1) return num
  return fallback
}

/** Vue Router may pass query values as string | string[]. */
function scalarQueryValue(value) {
  if (Array.isArray(value)) return value[0]
  return value
}

export function normalizeCarsFilters(value = {}) {
  const defaults = defaultCarsFilters()
  const source = value && typeof value === 'object' ? value : {}
  const page = Number(scalarQueryValue(source.page))
  const query = scalarQueryValue(source.query)
  const queryCarNumber = scalarQueryValue(source.queryCarNumber)
  const queryVIN = scalarQueryValue(source.queryVIN)
  const queryPhoneNumber = scalarQueryValue(source.queryPhoneNumber)
  return {
    query: typeof query === 'string' ? query : defaults.query,
    queryCarNumber:
      typeof queryCarNumber === 'string' ? queryCarNumber : defaults.queryCarNumber,
    queryVIN: typeof queryVIN === 'string' ? queryVIN : defaults.queryVIN,
    queryPhoneNumber:
      typeof queryPhoneNumber === 'string'
        ? queryPhoneNumber
        : defaults.queryPhoneNumber,
    autocodeStatus: normalizeAutocodeStatus(
      scalarQueryValue(source.autocodeStatus),
      defaults.autocodeStatus,
    ),
    page: Number.isFinite(page) && page > 0 ? page : defaults.page,
  }
}

/** Стабильное сравнение UI-фильтров (после normalize). */
export function carsFiltersEqual(a, b) {
  return JSON.stringify(normalizeCarsFilters(a)) === JSON.stringify(normalizeCarsFilters(b))
}

export function filtersFromRouteQuery(query = {}) {
  return normalizeCarsFilters(query)
}

export function hasCarsRouteFilters(query = {}) {
  return Boolean(
    query.query
    || query.queryCarNumber
    || query.queryVIN
    || query.queryPhoneNumber
    || query.autocodeStatus != null
    || query.page,
  )
}

export function loadCarsFilters(storage) {
  try {
    const parsed = JSON.parse(storage?.getItem(CARS_FILTERS_STORAGE_KEY) || 'null')
    if (parsed?.version !== CARS_FILTERS_VERSION) return defaultCarsFilters()
    return normalizeCarsFilters(parsed.value)
  } catch {
    return defaultCarsFilters()
  }
}

export function saveCarsFilters(storage, filters) {
  storage?.setItem(
    CARS_FILTERS_STORAGE_KEY,
    JSON.stringify({
      version: CARS_FILTERS_VERSION,
      value: normalizeCarsFilters(filters),
    }),
  )
}

/** URL имеет приоритет над localStorage. */
export function resolveInitialCarsFilters(routeQuery, storage) {
  if (hasCarsRouteFilters(routeQuery)) return filtersFromRouteQuery(routeQuery)
  return loadCarsFilters(storage)
}

export function filtersToRouteQuery(filters) {
  const query = {}
  if (filters.query) query.query = filters.query
  if (filters.queryCarNumber) query.queryCarNumber = filters.queryCarNumber
  if (filters.queryVIN) query.queryVIN = filters.queryVIN
  if (filters.queryPhoneNumber) query.queryPhoneNumber = filters.queryPhoneNumber
  const autocodeStatus = normalizeAutocodeStatus(filters.autocodeStatus)
  if (autocodeStatus === 0 || autocodeStatus === -1) {
    query.autocodeStatus = String(autocodeStatus)
  }
  if (filters.page && filters.page !== 1) query.page = String(filters.page)
  return query
}

export function buildCarsListParams(filters) {
  const params = {
    page: Math.max(1, Number(filters.page) || 1),
    per_page: CARS_PAGE_SIZE,
  }

  if (filters.query) params.search = String(filters.query).toLowerCase()
  if (filters.queryCarNumber) params.number = String(filters.queryCarNumber).toLowerCase()
  if (filters.queryVIN) params.vin = String(filters.queryVIN).toLowerCase()
  if (filters.queryPhoneNumber) {
    params.owner_phone = String(filters.queryPhoneNumber).toLowerCase()
  }

  const autocodeStatus = normalizeAutocodeStatus(filters.autocodeStatus)
  if (autocodeStatus === 0) params.avtocod_is_end = true
  else if (autocodeStatus === -1) params.avtocod_is_end = false

  return params
}

/** Сравнение params без page — для placeholderData / debounce. */
export function carsListParamsWithoutPage(params = {}) {
  const { page, ...rest } = params || {}
  return rest
}

export function carsListItems(payload) {
  if (!payload || typeof payload !== 'object') return []
  if (Array.isArray(payload.cars)) return payload.cars
  if (Array.isArray(payload.items)) return payload.items
  return []
}

export function carSecurityInfo(car) {
  const info = car?.security_info
  if (Array.isArray(info)) return info[0] || null
  return info || null
}

export function createCarsColumns() {
  return [
    {
      id: 'number',
      header: CARS_COLUMN_LABELS.number,
      accessorKey: 'number',
      size: 140,
      minSize: 100,
      maxSize: 280,
      enableSorting: false,
    },
    {
      id: 'avtocod_status',
      header: CARS_COLUMN_LABELS.avtocod_status,
      accessorFn: (row) => Boolean(carSecurityInfo(row)?.avtocod_is_end),
      size: 180,
      minSize: 140,
      maxSize: 280,
      enableSorting: false,
    },
    {
      id: 'black_list',
      header: CARS_COLUMN_LABELS.black_list,
      accessorFn: (row) => Boolean(carSecurityInfo(row)?.black_list),
      size: 120,
      minSize: 90,
      maxSize: 180,
      enableSorting: false,
    },
    {
      id: 'black_list_client',
      header: CARS_COLUMN_LABELS.black_list_client,
      accessorFn: (row) => Boolean(carSecurityInfo(row)?.black_list_client),
      size: 130,
      minSize: 100,
      maxSize: 200,
      enableSorting: false,
    },
    {
      id: 'ownerPhone',
      header: CARS_COLUMN_LABELS.ownerPhone,
      accessorKey: 'ownerPhone',
      size: 180,
      minSize: 120,
      maxSize: 280,
      enableSorting: false,
    },
    {
      id: 'vin',
      header: CARS_COLUMN_LABELS.vin,
      accessorKey: 'vin',
      size: 180,
      minSize: 120,
      maxSize: 320,
      enableSorting: false,
    },
  ]
}

export function normalizeCarsTableState(value = {}) {
  const source = value && typeof value === 'object' ? value : {}
  const known = new Set(CARS_COLUMN_IDS)
  const order = Array.isArray(source.columnOrder)
    ? [...new Set(source.columnOrder.filter((id) => known.has(id)))]
    : []
  const missing = CARS_COLUMN_IDS.filter((id) => !order.includes(id))

  const visibility = {}
  if (source.columnVisibility && typeof source.columnVisibility === 'object') {
    CARS_COLUMN_IDS.forEach((id) => {
      if (typeof source.columnVisibility[id] === 'boolean') {
        visibility[id] = source.columnVisibility[id]
      }
    })
  }
  if (CARS_COLUMN_IDS.every((id) => visibility[id] === false)) {
    visibility[CARS_COLUMN_IDS[0]] = true
  }

  const sizing = {}
  if (source.columnSizing && typeof source.columnSizing === 'object') {
    CARS_COLUMN_IDS.forEach((id) => {
      const size = Number(source.columnSizing[id])
      if (Number.isFinite(size) && size >= 40 && size <= 600) sizing[id] = size
    })
  }

  return {
    columnOrder: [...order, ...missing],
    columnVisibility: visibility,
    columnSizing: sizing,
  }
}

export function loadCarsTableState(storage) {
  try {
    const parsed = JSON.parse(storage?.getItem(CARS_TABLE_STORAGE_KEY) || 'null')
    if (parsed?.version !== CARS_TABLE_STATE_VERSION) {
      return normalizeCarsTableState({})
    }
    return normalizeCarsTableState(parsed.value)
  } catch {
    return normalizeCarsTableState({})
  }
}

export function saveCarsTableState(storage, state) {
  storage?.setItem(
    CARS_TABLE_STORAGE_KEY,
    JSON.stringify({
      version: CARS_TABLE_STATE_VERSION,
      value: normalizeCarsTableState(state),
    }),
  )
}
